import {
	OrderShippingMethod,
	useAddOrderQuoteOfferDeliveryMutation,
	useAddOrderQuoteOfferDeliverySplitMutation,
	useRemoveOrderQuoteOfferDeliveryMutation,
	useUpdateOrderQuoteOfferDeliveryMutation,
	useUpdateOrderQuoteOfferDeliverySplitMutation,
} from '@codezo/graphql';
import { Box, Check } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';

import { ProductImage } from '../../ui/product-image';
import { cn } from '../../../lib/utils';

import { useCurrencyStore } from '../../../stores/currency';
import {
	deliveryDayOrNull,
	deliveryMoney,
	isDeliveryIncomplete,
	isDeliveryOffered,
} from './delivery-rules';
import { METHODS, TierDeliverySection } from './TierDeliverySection';
import {
	type SplitDraft,
	isSplitBlank,
	isSplitIncomplete,
	isSplitWritable,
	legQuantities,
} from './split-rules';
import type { Batch, QuoteItem } from '../offer-components/types';

/* ════════════════════════ DELIVERY EDITOR ════════════════════════ */

/**
 * One (tier × method) cell of the grid this modal edits.
 *
 * There is no on/off flag — "Leave blank to skip a method" is literal. A method
 * is offered once it is priced, and clearing the price removes it on save. The
 * lead time is optional: price it now, fill the days in when the forwarder
 * answers.
 */
interface Slot {
	/** Delivery id when it already exists server-side; null when it doesn't. */
	existingId: string | null;
	price: string;
	deliveryDaysMin: string;
	deliveryDaysMax: string;
}

const slotKey = (pricingId: string, method: OrderShippingMethod) => `${pricingId}|${method}`;

const emptySlot = (): Slot => ({
	existingId: null,
	price: '',
	deliveryDaysMin: '',
	deliveryDaysMax: '',
});

/**
 * These mutations resolve with `{ success: false }` on a rejected write rather
 * than throwing, so an unchecked promise reports a refusal as a success. The
 * `message` is already translated — surface it as is.
 */
const assertOk = (res: { success: boolean; message: string } | null | undefined) => {
	if (!res?.success) throw new Error(res?.message || 'Unknown error');
};

const primaryImage = (item: QuoteItem | undefined) => {
	const images = item?.productImages;
	if (!images?.length) return null;
	return images.find((img) => img.isPrimary) ?? images[0];
};

interface DeliveryEditorModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	batch: Batch;
	itemLookup: Map<string, QuoteItem>;
	/** Quote-level deadline — the feasibility fallback when an item has none. */
	quoteDeadline: string | null;
	onSuccess: () => void;
}

/**
 * Sets shipping across the whole batch in one place: every product, every
 * quantity it was priced at, both methods.
 *
 * Shipping is a grid — product × quantity × method — and editing one cell at a
 * time hid the gaps. Here an unfilled combination is visible as an empty form
 * rather than as something the admin has to remember to go looking for. The
 * client's chosen quantity decides which of these they end up seeing.
 *
 * Laid out like the create-offer wizard's pricing step, because it is the same
 * job on the same shape of data — product rail, quantity tabs, one form per
 * method. Edits are local until Done, which diffs against the server and fires
 * one add/update/remove per changed cell.
 */
export function DeliveryEditorModal({
	open,
	onOpenChange,
	batch,
	itemLookup,
	quoteDeadline,
	onSuccess,
}: DeliveryEditorModalProps) {
	const { selectedCurrency: currency } = useCurrencyStore();
	const [addDelivery] = useAddOrderQuoteOfferDeliveryMutation();
	const [updateDelivery] = useUpdateOrderQuoteOfferDeliveryMutation();
	const [removeDelivery] = useRemoveOrderQuoteOfferDeliveryMutation();
	const [addSplit] = useAddOrderQuoteOfferDeliverySplitMutation();
	const [updateSplit] = useUpdateOrderQuoteOfferDeliverySplitMutation();
	const [saving, setSaving] = useState(false);

	// Only priced tiers can carry a delivery — a delivery hangs off a pricing row.
	const products = useMemo(
		() =>
			batch.offers
				.filter((o) => (o.pricing ?? []).length > 0)
				.map((offer) => {
					const item = itemLookup.get(offer.orderQuoteItemId);
					return {
						offer,
						item,
						name: item?.productName || offer.name,
						sku: item?.productSku ?? null,
						image: primaryImage(item),
						deadline: item?.deadline ?? quoteDeadline,
						tiers: (offer.pricing ?? []).map((pricing) => ({
							pricing,
							quantity:
								item?.quantities.find(
									(q) => q.id === pricing.orderQuoteItemQuantityId
								)?.quantity ?? 0,
						})),
					};
				}),
		[batch.offers, itemLookup, quoteDeadline]
	);

	const [slots, setSlots] = useState<Record<string, Slot>>({});
	/* Splits are per TIER, not per (tier x method): a tier can carry several, and
	   each one names its own methods. So they key off the pricing alone. */
	const [splitsByPricing, setSplitsByPricing] = useState<Record<string, SplitDraft[]>>({});
	const [activeOfferId, setActiveOfferId] = useState<string | null>(null);

	useEffect(() => {
		if (!open) return;
		const next: Record<string, Slot> = {};
		const nextSplits: Record<string, SplitDraft[]> = {};
		for (const product of products) {
			for (const { pricing } of product.tiers) {
				/* A split's own shippingMethod is DERIVED from its first leg, so it
				   answers a lookup by method. Kept out of the grid below, which is
				   the plain Standard/Express pair and nothing else. */
				const plain = (pricing.deliveries ?? []).filter((d) => !d.isSplit);
				nextSplits[pricing.id] = (pricing.deliveries ?? [])
					.filter((d) => d.isSplit)
					.map((split) => ({
						existingId: split.id,
						serverValid: split.isValid,
						legs: [...(split.parts ?? [])]
							.sort((a, b) => a.sequence - b.sequence)
							.map((part) => ({
								key: `${split.id}:${part.id}`,
								method: part.shippingMethod,
								quantity: String(part.quantity),
								price: part.totalPrice != null ? String(part.totalPrice) : '',
								deliveryDaysMin:
									part.deliveryDaysMin != null
										? String(part.deliveryDaysMin)
										: '',
								deliveryDaysMax:
									part.deliveryDaysMax != null
										? String(part.deliveryDaysMax)
										: '',
							})),
					}));
				for (const method of METHODS) {
					const existing = plain.find((d) => d.shippingMethod === method);
					next[slotKey(pricing.id, method)] = existing
						? {
								existingId: existing.id,
								price:
									existing.totalPrice != null ? String(existing.totalPrice) : '',
								deliveryDaysMin:
									existing.deliveryDaysMin != null
										? String(existing.deliveryDaysMin)
										: '',
								deliveryDaysMax:
									existing.deliveryDaysMax != null
										? String(existing.deliveryDaysMax)
										: '',
							}
						: emptySlot();
				}
			}
		}
		setSlots(next);
		setSplitsByPricing(nextSplits);
		const first = products[0];
		setActiveOfferId(first?.offer.id ?? null);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- (re)initialize only when the modal opens
	}, [open]);

	const activeProduct = products.find((p) => p.offer.id === activeOfferId) ?? products[0] ?? null;

	/* What the rail says under a product's name — how far its shipping has got,
	   counted the way the save counts it. "Priced" alone would call a product
	   done while a tier it was priced at still has no method on it. */
	type Product = (typeof products)[number];
	const tierIsOffered = (pricingId: string) =>
		METHODS.some((m) => isDeliveryOffered(slots[slotKey(pricingId, m)]));
	const railComplete = (product: Product) =>
		product.tiers.length > 0 && product.tiers.every(({ pricing }) => tierIsOffered(pricing.id));
	const railStatus = (product: Product) => {
		if (product.tiers.length === 0) return 'Not priced';
		const done = product.tiers.filter(({ pricing }) => tierIsOffered(pricing.id)).length;
		const splits = product.tiers.reduce(
			(sum, { pricing, quantity }) =>
				sum +
				(splitsByPricing[pricing.id] ?? []).filter(
					(split) => !isSplitBlank(split) && isSplitWritable(split, quantity)
				).length,
			0
		);
		const head =
			done === product.tiers.length ? 'Priced' : `${done}/${product.tiers.length} priced`;
		if (splits === 0) return head;
		return `${head} · ${splits} split${splits === 1 ? '' : 's'}`;
	};

	const patchSlot = (key: string, patch: Partial<Slot>) =>
		setSlots((prev) => ({ ...prev, [key]: { ...(prev[key] ?? emptySlot()), ...patch } }));

	/** Half-filled methods or splits anywhere in the batch. They hold up the save
	 * — and since every tier of the active product is on screen at once, the one
	 * at fault is visible rather than hidden behind a tab. */
	const incomplete = useMemo(
		() =>
			products.flatMap((product) =>
				product.tiers.filter(
					({ pricing, quantity }) =>
						METHODS.some((method) =>
							isDeliveryIncomplete(slots[slotKey(pricing.id, method)])
						) ||
						(splitsByPricing[pricing.id] ?? []).some((split) =>
							isSplitIncomplete(split, quantity)
						)
				)
			),
		[products, slots, splitsByPricing]
	);

	/* ── Commit ── */

	const handleDone = async () => {
		if (incomplete.length > 0) return;
		setSaving(true);
		const work: Array<Promise<unknown>> = [];

		for (const product of products) {
			for (const { pricing, quantity } of product.tiers) {
				for (const method of METHODS) {
					const slot = slots[slotKey(pricing.id, method)];
					if (!slot) continue;
					const existing = (pricing.deliveries ?? []).find(
						(d) => d.id === slot.existingId
					);
					const offered = isDeliveryOffered(slot);

					if (offered && !slot.existingId) {
						work.push(
							addDelivery({
								variables: {
									input: {
										orderQuoteOfferPricingId: pricing.id,
										shippingMethod: method,
										currency,
										price: deliveryMoney(slot.price),
										deliveryDaysMin: deliveryDayOrNull(slot.deliveryDaysMin),
										deliveryDaysMax: deliveryDayOrNull(slot.deliveryDaysMax),
										quantity,
									},
								},
							}).then(({ data }) => assertOk(data?.addOrderQuoteOfferDelivery))
						);
					} else if (offered && slot.existingId && existing) {
						// Compared as null-or-number on both sides: blanking the days of a
						// saved option is a real edit, and reading '' as 0 would both hide
						// it and promise same-day arrival.
						const nextDaysMin = deliveryDayOrNull(slot.deliveryDaysMin);
						const nextDaysMax = deliveryDayOrNull(slot.deliveryDaysMax);
						const changed =
							deliveryMoney(slot.price) !== (existing.totalPrice ?? 0) ||
							nextDaysMin !== (existing.deliveryDaysMin ?? null) ||
							nextDaysMax !== (existing.deliveryDaysMax ?? null);
						if (changed) {
							work.push(
								updateDelivery({
									variables: {
										id: slot.existingId,
										input: {
											currency,
											price: deliveryMoney(slot.price),
											deliveryDaysMin: nextDaysMin,
											deliveryDaysMax: nextDaysMax,
										},
									},
								}).then(({ data }) => assertOk(data?.updateOrderQuoteOfferDelivery))
							);
						}
					} else if (!offered && slot.existingId) {
						// Cleared price on something that exists — stop offering it.
						work.push(
							removeDelivery({ variables: { id: slot.existingId } }).then(
								({ data }) => assertOk(data?.removeOrderQuoteOfferDelivery)
							)
						);
					}
				}

				/* ── Splits on this tier ── */
				const drafts = splitsByPricing[pricing.id] ?? [];
				const kept = new Set(
					drafts.map((split) => split.existingId).filter((id): id is string => !!id)
				);

				for (const split of drafts) {
					// An untouched split was added and never filled in. Nothing to
					// write, and nothing the admin would recognise as a loss.
					if (isSplitBlank(split) && !split.existingId) continue;
					if (!isSplitWritable(split, quantity)) continue;

					// Legs go in the order they are shown: the backend assigns
					// sequence from the array, so position IS the leg's identity.
					//
					// Quantities come from `legQuantities`, where the last leg is the
					// remainder — the same figure the card showed as "the rest", so
					// what is sent is what was read.
					//
					// Days are the leg's OWN — a tier may offer only Standard on its
					// own and still carry a split with an Express half, so there is
					// not always a method to take them from.
					const quantities = legQuantities(split, quantity);
					const parts = split.legs.map((leg, i) => ({
						shippingMethod: leg.method,
						quantity: quantities[i] ?? 0,
						price: deliveryMoney(leg.price),
						currency,
						deliveryDaysMin: deliveryDayOrNull(leg.deliveryDaysMin),
						deliveryDaysMax: deliveryDayOrNull(leg.deliveryDaysMax),
					}));

					if (split.existingId) {
						/* Parts replace the existing legs wholesale and the option
						   keeps its id — which is what makes this an update rather
						   than a delete-and-add: a client who already picked this
						   split keeps their selection. */
						work.push(
							updateSplit({
								variables: { id: split.existingId, input: { parts } },
							}).then(({ data }) =>
								assertOk(data?.updateOrderQuoteOfferDeliverySplit)
							)
						);
					} else {
						work.push(
							addSplit({
								variables: {
									input: { orderQuoteOfferPricingId: pricing.id, parts },
								},
							}).then(({ data }) => assertOk(data?.addOrderQuoteOfferDeliverySplit))
						);
					}
				}

				// Splits the admin deleted. A split is removed through the plain
				// delivery mutation — it is a delivery row like any other.
				for (const saved of (pricing.deliveries ?? []).filter((d) => d.isSplit)) {
					if (kept.has(saved.id)) continue;
					work.push(
						removeDelivery({ variables: { id: saved.id } }).then(({ data }) =>
							assertOk(data?.removeOrderQuoteOfferDelivery)
						)
					);
				}
			}
		}

		try {
			if (work.length > 0) {
				await Promise.all(work);
				toast.success(
					work.length === 1
						? 'Delivery updated'
						: `${work.length} delivery options updated`
				);
			}
			onOpenChange(false);
			onSuccess();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to save delivery');
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			{/* Wider than the 3xl it was on tabs: the pane now carries every tier of
			    a product at once, and each of those is two method cards side by
			    side. At 3xl the rail left ~590px for a pair that wants ~640, so
			    the second card was clipped by a pane that only scrolls vertically. */}
			<DialogContent className="sm:max-w-4xl">
				{/* DialogHeader is `pb-0` — it normally borrows its bottom gap from
				    DialogBody, which the two-pane row below replaces. */}
				<DialogHeader className="pb-4">
					<DialogTitle>Delivery</DialogTitle>
					<DialogDescription>
						Price Standard and Express for every quantity. Splits are optional extras.
					</DialogDescription>
				</DialogHeader>

				{/* Sized like every other scrollable modal body here — a 65vh cap, so
				    it follows the screen instead of a number typed for one laptop. */}
				<div className="flex max-h-[65vh] min-h-80 flex-1 overflow-hidden border-t border-line-1">
					{/* Product rail */}
					<div className="scroll-thin flex w-44 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-line-1 p-2">
						{products.map((product) => (
							<button
								key={product.offer.id}
								type="button"
								onClick={() => setActiveOfferId(product.offer.id)}
								className={cn(
									'flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
									product.offer.id === activeProduct?.offer.id
										? 'bg-accent-soft ring-1 ring-accent-line'
										: 'hover:bg-surface-hover'
								)}
							>
								{product.image?.url ? (
									<ProductImage
										image={product.image}
										alt={product.name}
										className="h-9 w-9 shrink-0 rounded-[var(--r-2)] border border-line-1"
									/>
								) : (
									<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-2)] border border-line-1 bg-surface-fill">
										<Box size={14} className="text-text-4" />
									</div>
								)}
								<div className="min-w-0 flex-1">
									<div className="truncate text-sm font-medium text-text-1">
										{product.name}
									</div>
									<div className="truncate text-2xs text-text-4">
										{railStatus(product)}
									</div>
								</div>
								{railComplete(product) && (
									<Check size={12} className="shrink-0 text-text-4" />
								)}
							</button>
						))}
					</div>

					{/* Active product — every tier it was priced at, stacked.
					    They used to sit behind quantity tabs, which hid exactly what
					    this editor exists to show: an unpriced quantity is something
					    you have to SEE, not remember to go looking for. */}
					<div className="scroll-thin min-h-0 min-w-0 flex-1 overflow-y-auto">
						{activeProduct ? (
							<>
								{/* `bg-popover` is the dialog's own background, not a fill token: a sticky
								    header has to be OPAQUE, and anything translucent lets the
								    tier headings scroll visibly through it. */}
								<div className="sticky top-0 z-10 flex items-center gap-2 border-b border-line-1 bg-popover px-4 py-2.5">
									<span className="text-base font-medium text-text-1">
										{activeProduct.name}
									</span>
									{activeProduct.sku && (
										<span className="truncate font-mono text-2xs text-text-4">
											{activeProduct.sku}
										</span>
									)}
								</div>

								{activeProduct.tiers.length === 0 ? (
									<div className="p-6 text-center text-sm text-text-4">
										Nothing is priced on this product yet. Set pricing before
										adding delivery.
									</div>
								) : (
									<div className="flex flex-col gap-5 p-4">
										{activeProduct.tiers.map(({ pricing, quantity }) => (
											<TierDeliverySection
												key={pricing.id}
												quantity={quantity}
												pricing={pricing}
												slots={Object.fromEntries(
													METHODS.map((method) => [
														method,
														slots[slotKey(pricing.id, method)] ??
															emptySlot(),
													])
												)}
												splits={splitsByPricing[pricing.id] ?? []}
												currency={currency}
												deadline={activeProduct.deadline}
												acceptDeadline={batch.acceptDeadline ?? null}
												onPatchSlot={(method, patch) =>
													patchSlot(slotKey(pricing.id, method), patch)
												}
												onChangeSplits={(next) =>
													setSplitsByPricing((prev) => ({
														...prev,
														[pricing.id]: next,
													}))
												}
											/>
										))}
									</div>
								)}
							</>
						) : (
							<div className="flex h-full items-center justify-center p-6 text-center text-sm text-text-4">
								Nothing is priced yet. Set pricing on a product before adding
								delivery.
							</div>
						)}
					</div>
				</div>

				<DialogFooter className="items-center sm:justify-between">
					{/* The one thing the arrival lines everywhere above depend on, said
					    once at the foot rather than repeated under each of them. */}
					<span className="text-2xs text-text-4">
						Arrival dates count from when production is ready
					</span>
					<div className="flex items-center gap-2">
						<Button
							variant="secondary"
							onClick={() => onOpenChange(false)}
							disabled={saving}
						>
							Cancel
						</Button>
						<Button onClick={handleDone} disabled={saving || incomplete.length > 0}>
							<Check size={13} />
							{saving ? 'Saving…' : 'Save delivery'}
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
